import React from "react";
import { Tile } from "@carbon/react";
import i18next from "../../i18n";

import { OrderResponse } from "../../types";
import {
  formatDate,
  fileText,
  orderPlace,
  orderPlaceLabelKey,
} from "../../utils";
import FileReader from "../../components/FileReader/FileReader";

/**
 * The read-only "Buyer's information + commercial conditions" panel the seller
 * reads to prepare a quote.
 *
 * RULING 4 (Task 5): the pre-Carbon panel also rendered the buyer's Tax ID
 * (`order.company.taxId`) and phone (`order.user.phone`). Those two fields are
 * NOT on the lean `OrderResponse` projection the seller receives — the backend
 * deliberately strips `company.taxId` / `user.phone` (and the wider
 * company/user graph) from the seller-facing DTO, so they render blank. This is
 * a privacy boundary, not a data-loading bug: the seller was never meant to see
 * the buyer's Tax ID or phone. We DROP those two rows rather than widen the
 * order-ownership guard to re-expose them. Everything kept here IS on the lean
 * projection (company name, buyer name + email, and the order-level commercial
 * conditions the seller needs to quote), plus how many items the seller is
 * quoting (SCRUM-36).
 */
const BuyerConditionsPanel: React.FC<{
  order: OrderResponse;
  itemCount: number;
}> = ({ order, itemCount }) => (
  <Tile className="offer-buyer">
    <p className="offer-buyer__heading">{i18next.t("offer.buyerConditions")}</p>
    <dl className="offer-buyer__grid">
      <dt>{i18next.t("offer.buyerCompany")}</dt>
      <dd className="offer-buyer__company">{order?.company?.name}</dd>

      <dt>{i18next.t("offer.buyerName")}</dt>
      <dd>{order?.user?.fullName}</dd>

      <dt>{i18next.t("offer.buyerEmail")}</dt>
      <dd>{order?.user?.email}</dd>
    </dl>

    <hr className="offer-buyer__divider" />

    <p className="offer-buyer__subheading">
      {i18next.t("offer.comercialConditions")}
    </p>
    <dl className="offer-buyer__grid">
      <dt>{i18next.t("offer.buyerSupplyPeriod")}</dt>
      <dd>
        {formatDate(order?.startSupplyDate)} -{" "}
        {formatDate(order?.endSupplyDate)}
      </dd>

      {order?.incoTerm && (
        <>
          <dt>{i18next.t("offer.buyerIncoTerm")}</dt>
          <dd>{order?.incoTerm}</dd>
        </>
      )}

      <dt>{i18next.t("tenders.form.paymentMethod")}</dt>
      <dd>
        {order?.paymentMethod} - {order?.paymentTerm}{" "}
        {i18next.t("analytics.days")}
      </dd>

      <dt>{i18next.t(orderPlaceLabelKey(order))}</dt>
      <dd>
        {order?.buyerPicksUp
          ? i18next.t("offer.buyerPicksUp")
          : orderPlace(order)}
      </dd>

      <dt>{i18next.t("offer.buyerItemCount")}</dt>
      <dd>{itemCount}</dd>

      {order?.comment && (
        <>
          <dt>{i18next.t("offer.buyerComments")}</dt>
          <dd>{order?.comment}</dd>
        </>
      )}

      {order?.files?.length > 0 && (
        <>
          <dt>{i18next.t("offer.buyerFiles")}</dt>
          <dd className="offer-buyer__files">
            {order?.files.map((file) => (
              <FileReader
                key={file.fileURL}
                fileKey={file.fileURL}
                text={fileText(file.type)}
              />
            ))}
          </dd>
        </>
      )}
    </dl>
  </Tile>
);

export default BuyerConditionsPanel;
